import { Box, CircularProgress } from '@material-ui/core';
import { getNotificationsByExternalId, Loan } from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction } from 'react';

import { LoadingValuesWarning } from '@/components/modules/Documentation/components/shared/LoadingValuesWarning';
import { NotificationsTable } from '@/components/modules/Notifications/components/NotificationsTable';
import { Font } from '@/components/shared/components/Font';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { PendingDataType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';

interface NotificationsDialogProps {
  loan: Loan;
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
}

export const NotificationsDialog = ({ loan, opened, setOpened }: NotificationsDialogProps) => {
  const { data: notifications, loading } = useCache(
    getNotificationsByExternalId,
    { externalId: loan.id },
    { fallbackData: undefined },
  );

  const handleClose = () => {
    setOpened(false);
  };

  return (
    <NemuruDialog
      avoidDefaultClose
      opened={opened}
      setOpened={setOpened}
      buttons={
        <>
          <StandardButton disabled={loading} onClick={handleClose}>
            <Text tId={'CLOSE'} />
          </StandardButton>
        </>
      }
      dialogProps={{ maxWidth: 'lg' }}
    >
      <Box mb={4}>
        <Font variant={'h2'} color={'title'}>
          <Text strong tId={'NOTIFICATIONS'} />
        </Font>
      </Box>
      {loading ? (
        <LoadingValuesWarning
          messageTextId="LOADING_NOTIFICATIONS"
          loadingElement={<CircularProgress size={18} color="secondary" />}
          boxProps={{ height: '100px' }}
        />
      ) : !notifications || notifications.length === 0 || !loan ? (
        <>
          <PendingDataWarning type={PendingDataType.NO_NOTIFICATIONS} />
        </>
      ) : (
        <>
          <NotificationsTable notifications={notifications} loan={loan} />
        </>
      )}
    </NemuruDialog>
  );
};
